import React, { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import AsyncButton from "../../common/components/AsyncButton";
import Managed, { useManaged } from "../../common/components/Managed";
import StaticTable from "../../common/components/StaticTable";
import api from "../../common/lib/api";
import auth from "../../common/lib/auth";

const DEFAULT_STATE = null;

export const UPGRADE_FIELDS = [
  { id: "orderNumber", name: "Order", defaultOrder: -1 },
  { id: "soldAt", name: "Date", type: "datetime" },
  { id: "upgradeCode", name: "Upgrade Code" },
  { id: "status", name: "Status" },
  { id: "nominatedDeviceId", name: "Device" },
  { id: "enforcementStatus", name: "Enforcement Status" },
];

function Section({ children }) {
  return (
    <div
      style={{
        display: "inline-block",
        background: "white",
        padding: "0 .5em .5em .5em",
        margin: "0 0.5em 0.5em 0",
        verticalAlign: "top",
      }}
    >
      {children}
    </div>
  );
}

export default function Page() {
  const [state, setState, setError] = useManaged(DEFAULT_STATE);

  const { companyId } = useParams();

  const load = useCallback(async (companyId) => {
    const {
      data: { data },
    } = await api.get("/companies/item/" + companyId);

    if (auth.withAllOf("accounting_bill_upgrades")) {
      const {
        data: { data: upgrades },
      } = await api.get("/upgrades/company/" + companyId);
      data.upgrades = upgrades;
    }

    if (auth.withAllOf("accounting_link_company")) {
      const {
        data: { data: linkedCompanies },
      } = await api.get("/companies-link/company/" + companyId);
      data.linkedCompanies = linkedCompanies;
    }

    return data;
  }, []);

  useEffect(() => {
    load(companyId).then(setState, setError);
  }, [companyId, load, setError, setState]);

  const company = state.data || {};

  return (
    <Managed state={state}>
      <h1>{company.name}</h1>
      <Section>
        <h3>Email</h3>
        <p>{company.email}</p>
      </Section>
      <Section>
        <h3>Addresses</h3>
        <ul>
          {(company.addresses || []).map((address) => (
            <li>
              <pre>{address}</pre>
            </li>
          ))}
        </ul>
      </Section>
      {company.specialPrices && (
        <Section>
          <h3>Special Prices</h3>
          <table className="bp3-html-table bp3-html-table-bordered">
            <thead>
              <tr>
                <th>Item</th>
                <th>If qty &gt;=</th>
                <th>Special price</th>
              </tr>
            </thead>
            <tbody>
              {company.specialPrices.map((price) => {
                return (
                  <tr>
                    <td>{price.saleItemId}</td>
                    <td>{price.minQuantity}</td>
                    <td>{price.unitPrice}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}
      {company.taxCode && (
        <Section>
          <h3>Tax Code</h3>
          <p>
            {company.taxCode.code} {company.taxCode.rate}%
          </p>
        </Section>
      )}

      {company.freightTaxCode && (
        <Section>
          <h3>Freight Tax Code</h3>
          <p>
            {company.freightTaxCode.code} {company.freightTaxCode.rate}%
          </p>
        </Section>
      )}

      {company.terms && (
        <Section>
          <h3>Payment Terms</h3>
          <p>{company.terms.paymentIsDue}</p>
          {company.terms.paymentIsDue.match(/NumberOfDays/) &&
            company.terms.balanceDueDate && (
              <p>{company.terms.balanceDueDate} days</p>
            )}
          {company.terms.paymentIsDue.match(/DayOfMonth/) &&
            company.terms.balanceDueDate && (
              <p>Day {company.terms.balanceDueDate}</p>
            )}
        </Section>
      )}

      {company.linkedCompanies && (
        <Section>
          <h3>Linked Companies</h3>
          <table className="bp3-html-table bp3-html-table-bordered">
            <thead>
              <tr>
                <th>System</th>
                <th>Company ID</th>
                <th>Company Name</th>
                <th>Company Type</th>
              </tr>
            </thead>
            <tbody>
              {company.linkedCompanies.map((linkedCompany) => (
                <tr>
                  <td>{linkedCompany.system}</td>
                  <td>{linkedCompany.companyId}</td>
                  <td>{linkedCompany.name}</td>
                  <td>{linkedCompany.type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {company.upgrades &&
        Object.entries(company.upgrades).map(([system, upgrades]) => {
          return (
            <Section key={`upgrades-${system}`}>
              <h3>Upgrades</h3>
              <table className="bp3-html-table bp3-html-table-bordered">
                <tbody>
                  <tr>
                    <td>Sold: {upgrades.sold || 0}</td>
                    <td>Set For Upgrade: {upgrades.nominated || 0}</td>
                    <td>Upgraded: {upgrades.upgraded || 0}</td>
                    <td>
                      Replaced Hardware Returned:{" "}
                      {upgrades.oldHardwareReturned || 0}
                    </td>
                    <th>Total Upgrades: {upgrades.total || 0}</th>
                  </tr>
                  <tr>
                    <th colSpan="2">
                      Total Not Upgraded: {upgrades.notUpgraded || 0} /{" "}
                      {upgrades.notUpgradedLimit || 0}
                      <>
                        <br />
                        <AsyncButton
                          small
                          onClick={async () => {
                            const newLimit = prompt(
                              "Please enter a new limit. Put in 0 or -1 to revert back to default.",
                              upgrades.notUpgradedLimit
                            );
                            if (newLimit !== null) {
                              // not cancelled
                              await api.post("/companies/item/" + companyId + "/upgrade-limit", {limit: parseInt(newLimit, 10)})

                              window.location.reload();
                            }
                          }}
                        >
                          Change Not Upgraded Limit
                        </AsyncButton>
                      </>
                    </th>
                    <th>
                      Total Not Returned: {upgrades.notReturned || 0} /{" "}
                      {upgrades.notReturnedLimit || 0}
                    </th>
                    <th colSpan="2">
                      Can Order: {upgrades.purchaseLimit || 0} more upgrades
                    </th>
                  </tr>
                </tbody>
              </table>
              <StaticTable
                items={upgrades.upgrades || []}
                fields={UPGRADE_FIELDS}
              />
            </Section>
          );
        })}
    </Managed>
  );
}
